import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { MemberSubscriptionStatus } from '@vista-digital/ocapi-types/v1';
import { observer } from 'mobx-react';
import { useContext, useReducer } from 'react';
import { useOccConfig } from '../../../context/occ/hooks/use-occ-config';
import { Button } from '../../../subcomponents/button/button';
import { ButtonColor } from '../../../subcomponents/button/types/color';
import { ButtonSize } from '../../../subcomponents/button/types/size';
import { CardExpirationStatus, getCardExpirationStatus } from '../../../utils/get-card-expiration-status';
import { MemberPaymentCardWalletContextInstance } from '../types/context';
import { memberPaymentCardWalletFormActionReducer } from '../utils/form/form-action-reducer';
import { MemberPaymentCardWalletFormActionType as ActionType } from '../utils/form/form-action-type';
import { defaultMemberPaymentCardWalletFormState } from '../utils/form/form-state';
import { MemberPaymentCardWalletConnector } from './connector';
import { MemberPaymentCardWalletMessage, MemberPaymentCardWalletMessageType } from './message';
import { MemberPaymentCardWalletAddCardModal, MemberPaymentCardWalletAddCardModalType } from './modals/add-card-modal';
const b = bemClassBuilder('v-member-payment-card-wallet-form');
export const MemberPaymentCardWalletForm = observer(props => {
    const { member: { paymentCards: paymentCardsConfig } } = useOccConfig();
    const { subscription, primaryCard, paymentMethodId, closeForm, showCancelButton } = props;
    const { displayText, config } = useContext(MemberPaymentCardWalletContextInstance);
    const [state, dispatch] = useReducer(memberPaymentCardWalletFormActionReducer, defaultMemberPaymentCardWalletFormState);
    const { showModal, modalType, error, paymentCardsClient, makePrimaryChosen, loadingState, termsAndConditionsAccepted, addPaymentCardButtonState } = state;
    const handleAddPaymentCardButtonClicked = () => {
        if (!paymentCardsClient)
            throw new Error('Payment cards client has not been initialised.');
        const isRecurringSubscription = subscription === null || subscription === void 0 ? void 0 : subscription.billingDetails.isRecurring;
        if (isRecurringSubscription && (subscription === null || subscription === void 0 ? void 0 : subscription.status) === MemberSubscriptionStatus.Frozen) {
            dispatch({
                type: ActionType.OpenModal,
                modalType: MemberPaymentCardWalletAddCardModalType.Reactivate
            });
            return;
        }
        if (config.requirePrimaryCard) {
            dispatch({ type: ActionType.MakePrimaryChosen });
            paymentCardsClient.startAddingPaymentCard();
            return;
        }
        const cardExpirationStatus = primaryCard &&
            getCardExpirationStatus(primaryCard.cardDetails, paymentCardsConfig.expiringSoonThresholdInMonths);
        if (primaryCard && cardExpirationStatus !== CardExpirationStatus.Expired) {
            paymentCardsClient.startAddingPaymentCard();
            return;
        }
        const updatedModalType = isRecurringSubscription
            ? MemberPaymentCardWalletAddCardModalType.MakePrimaryAndAcceptTermsAndConditions
            : MemberPaymentCardWalletAddCardModalType.MakePrimary;
        dispatch({ type: ActionType.OpenModal, modalType: updatedModalType });
    };
    const modalProps = {
        show: showModal,
        type: modalType,
        startAddingPaymentCard: paymentCardsClient === null || paymentCardsClient === void 0 ? void 0 : paymentCardsClient.startAddingPaymentCard,
        reactivationPrice: subscription === null || subscription === void 0 ? void 0 : subscription.billingDetails.reactivationPrice,
        onMakePrimaryChosen: () => dispatch({ type: ActionType.MakePrimaryChosen }),
        hideModal: () => dispatch({ type: ActionType.CloseModal })
    };
    const connectorProps = {
        paymentMethodId,
        termsAndConditionsAccepted,
        addPaymentCardButtonState,
        loadingState,
        showAddCardButton: paymentCardsClient && !paymentCardsClient.isButtonIncluded,
        showCancelButton,
        makePrimaryChosen,
        onAddPaymentCard: handleAddPaymentCardButtonClicked,
        onClose: closeForm,
        dispatch
    };
    const handleTryAgainButtonClicked = () => dispatch({ type: ActionType.TryAgainButtonClicked });
    const hasPaymentDeclined = error === MemberPaymentCardWalletMessageType.CardDeclined;
    if (error && !hasPaymentDeclined) {
        if (error === MemberPaymentCardWalletMessageType.AddCardFailed) {
            return (_jsx("div", Object.assign({ className: b.block() }, { children: _jsx(MemberPaymentCardWalletMessage, Object.assign({ messageType: error }, { children: _jsx(Button, { label: displayText.addCardFailedTryAgainButtonLabel, size: ButtonSize.Large, color: ButtonColor.Primary, onClick: handleTryAgainButtonClicked }) })) })));
        }
        return _jsx(MemberPaymentCardWalletMessage, { messageType: error });
    }
    return (_jsxs("div", Object.assign({ className: b.block() }, { children: [_jsx(MemberPaymentCardWalletAddCardModal, Object.assign({}, modalProps)), hasPaymentDeclined ? (_jsx(MemberPaymentCardWalletMessage, Object.assign({ messageType: MemberPaymentCardWalletMessageType.CardDeclined }, { children: _jsx(Button, { label: displayText.cardDeclinedTryAgainButtonLabel, size: ButtonSize.Large, color: ButtonColor.Primary, onClick: handleTryAgainButtonClicked }) }))) : (_jsx(MemberPaymentCardWalletConnector, Object.assign({}, connectorProps)))] })));
});
MemberPaymentCardWalletForm.displayName = 'MemberPaymentCardWalletForm';
